import { customElement } from 'lit/decorators.js';

import JobSchema from '../../../../models/job-schema.js';
import type Company from '../../../../models/company.js';
import type { PublicLedgerTopic } from '../../../../models/public-ledger.js';
import { ruleFromRequirement, type RuleGroup } from './signatory-change.js';
import NSUpdatePublicLedgerConfigKey, { type RuleChangeCopy } from './update-config-key.js';
import definition from '@job-schemas/public-ledger/update-topic-key.json';
import { jobSchema } from '../../job-schema.js';

/**
 * The requester's card of a change to who signs the company's topic messages (#38): the account change's rule editor,
 * for the topic's own submit key. "Request change" sends the rule to public-ledger-update-topic-submit-key, which checks
 * it again; the rule takes effect only once everyone who signs the topic now and everyone in the new rule have approved it
 * and it has been applied to the company's public ledger topic. "Cancel" ends the change.
 */
@customElement(NSUpdatePublicLedgerTopicKey.is)
export default class NSUpdatePublicLedgerTopicKey extends NSUpdatePublicLedgerConfigKey {
  static get is() {
    return 'update-public-ledger-topic-key';
  }

  static get schema(): JobSchema {
    return jobSchema(definition, {
      outcomes: { update: { type: 'hidden', label: 'Request change' }, cancel: { type: 'hidden', label: 'Cancel' } },
    });
  }

  /** The topic's rule now, from the card's plTopic (the company's topic when the card names none). */
  protected async _loadRule(company: Company): Promise<{ current: RuleGroup | null; targetId: string | null }> {
    const entityId = this._job?.inputValues?.entityId;
    const topicId = typeof entityId === 'string' && entityId ? entityId : (company.ledgerCred?.topicId ?? null);
    const topic = topicId
      ? ((await this._db?.getById('plTopic', topicId)) as PublicLedgerTopic | undefined)
      : undefined;
    return {
      current: ruleFromRequirement(topic?.submitKey),
      targetId: topic?.id ?? null,
    };
  }

  /** Send the new rule for checking and recording: public-ledger-update-topic-submit-key. */
  protected async _propose(workflowId: string, targetId: string, rule: RuleGroup): Promise<void> {
    if (!this._pLedgerService) throw new Error('Missing required public ledger service');
    await this._pLedgerService.proposeTopicSignatureRule(workflowId, targetId, rule);
  }

  protected get _copy(): RuleChangeCopy {
    return {
      intro:
        "Choose who signs the company's public ledger topic messages. Each group needs a number of its signatories, " +
        'or of the groups inside it, to sign. Everyone who signs the topic now and the new signatories all approve ' +
        'this change.',
      requested:
        'The change has been requested. Anyone new without a public ledger account has been asked to create one, ' +
        'and then everyone who signs the topic now and everyone in the new rule will be asked to approve it. The new ' +
        "rule takes effect once both have enough approvals and it has been applied to the company's public ledger " +
        'topic, and you will be told how it ends.',
      noTarget: "The company's public ledger topic isn't set up yet",
    };
  }
}
